import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import {
  FLOOR_TILESET_PROFILES,
  SECRET_STATUE_TILE_TO_IMG,
  hash01,
  buildAOLayer,
  buildGrimeLayers,
} from '../map/tilesets';
import { RANSACKED_CHEST_TILE } from '../map/ransackedChest';
import {
  getSpecialBossFloorEnemyIdAtTile,
  MONSIGNOR_LIBERATED_GUARD_TILE,
} from '../boss/specialBossFloorConfig';
import {
  getEnemyIdForTile,
  CORRUPTED_SPECIAL_ENEMY_ID,
} from '../enemies/dungeonEnemySelection';


const LIGHT_BUCKET_HIDDEN = 0;
const LIGHT_BUCKET_EXPLORED_DARK = 1;
const LIGHT_BUCKET_VISIBLE_DIM = 2;
const LIGHT_BUCKET_VISIBLE_MID = 3;
const LIGHT_BUCKET_VISIBLE_BRIGHT = 4;

export function getTileLightBucket({
  revealed,
  isVisible,
  tile,
  torchProgress,
  lightRadius,
  playerDistance,
  isBrazierLitAtTile,
}) {
  const isLitBrazierTile = tile === 'brazier' && isBrazierLitAtTile;
  const isLightSource = isLitBrazierTile || tile === 'rest';

  if (!revealed) return LIGHT_BUCKET_HIDDEN;

  if (!isVisible) {
    return isLightSource ? LIGHT_BUCKET_VISIBLE_DIM : LIGHT_BUCKET_EXPLORED_DARK;
  }

  if (isLightSource) return LIGHT_BUCKET_VISIBLE_BRIGHT;

  if (torchProgress <= 0) return LIGHT_BUCKET_VISIBLE_DIM;

  const falloff = Math.max(0, 1 - playerDistance / (lightRadius + 0.85));

  if (falloff >= 0.72) return LIGHT_BUCKET_VISIBLE_BRIGHT;
  if (falloff >= 0.38) return LIGHT_BUCKET_VISIBLE_MID;
  return LIGHT_BUCKET_VISIBLE_DIM;
}

function isCommonDungeonTileType(tile) {
  return (
    tile !== 'non-walkable' &&
    tile !== 'hidden-corridor' &&
    tile !== 'enemy' &&
    tile !== 'enemy-boss' &&
    tile !== 'storyline'
  );
}

function isPrimeLabArtTile(tile) {
  return typeof tile === 'string' && tile.startsWith('corridor_lab');
}

function getPrimeLabTileImg(tile, tileAssets) {
  if (!isPrimeLabArtTile(tile) || !tileAssets) return null;

  return (
    {
      corridor_lab0a: tileAssets.corridorLab0aImg,
      corridor_lab0b: tileAssets.corridorLab0bImg,
      corridor_lab0c: tileAssets.corridorLab0cImg,
      corridor_lab0d: tileAssets.corridorLab0dImg,
      corridor_lab1a: tileAssets.corridorLab1aImg,
      corridor_lab1b: tileAssets.corridorLab1bImg,
      corridor_lab2a: tileAssets.corridorLab2aImg,
      corridor_lab2b: tileAssets.corridorLab2bImg,
      corridor_lab2c: tileAssets.corridorLab2cImg,
      corridor_lab2d: tileAssets.corridorLab2dImg,
      corridor_lab3a: tileAssets.corridorLab3aImg,
      corridor_lab3b: tileAssets.corridorLab3bImg,
      corridor_lab3c: tileAssets.corridorLab3cImg,
      corridor_lab3d: tileAssets.corridorLab3dImg,
      corridor_lab3e: tileAssets.corridorLab3eImg,
    }[tile] || null
  );
}


const StaticDungeonTileFace = React.memo(function StaticDungeonTileFace({
  x,
  y,
  tile,
  revealed,
  floorSeed,
  lowFx,
  tileVariation,
  currentFloorTilesetProfile,
  renderTileRadius,
  isCorruptedTile,
  isBrazierLitAtTile,
  tileAssets,
}) {
  const {
    corridorImg,
    corridorImgs,
    restImg,
    doorOpenImg,
    doorLockedImg,
    cardbackImg,
    chestOpenImg,
    chestLockedImg,
    chestSimpleClosedImg,
    chestSimpleLockedImg,
    chestSimpleOpenImg,
    chestRareClosedImg,
    chestRareLockedImg,
    chestRareOpenImg,
    brazierImg,
    brazierOutImg,
    merchantImg,
    shrineImg,
    wellImg,
    witchImg,
    stepsUpImg,
    stepsDownImg,
    loreImg,
    loreImg2,
    secretEntranceImg,
    damageMasks,
    damageMaskOverlays,
  } = tileAssets;

  const aoLayer = React.useMemo(() => buildAOLayer(), []);
  const grimeLayersMemo = React.useMemo(
    () => buildGrimeLayers(x, y, floorSeed),
    [x, y, floorSeed],
  );

  const isStoneTilesetTarget =
    tile === 'floor' ||
    tile === 'corridor' ||
    isPrimeLabArtTile(tile) ||
    tile === 'trap' ||
    tile === 'secret-entrance' ||
    (typeof tile === 'string' && tile.startsWith('statue-'));

  const cardbackDamageMaskIndex =
    Array.isArray(damageMasks) && damageMasks.length > 0
      ? Math.min(
          damageMasks.length - 1,
          Math.floor(hash01(x * 19 + 7, y * 23 + 13, floorSeed) * damageMasks.length),
        )
      : -1;

  const cardbackDamageMaskImg =
    cardbackDamageMaskIndex >= 0 && Array.isArray(damageMasks)
      ? damageMasks[cardbackDamageMaskIndex] || null
      : null;

  const cardbackDamageMaskOverlayImg =
    cardbackDamageMaskIndex >= 0 && Array.isArray(damageMaskOverlays)
      ? damageMaskOverlays[cardbackDamageMaskIndex] || null
      : null;

  const damageMaskIndex =
    isStoneTilesetTarget && cardbackDamageMaskIndex >= 0
      ? cardbackDamageMaskIndex
      : -1;

  const damageMaskImg =
    damageMaskIndex >= 0 && Array.isArray(damageMasks)
      ? damageMasks[damageMaskIndex] || null
      : null;

  const damageMaskOverlayImg =
    damageMaskIndex >= 0 && Array.isArray(damageMaskOverlays)
      ? damageMaskOverlays[damageMaskIndex] || null
      : null;

  const cardbackDamageMaskStyles = cardbackDamageMaskImg
    ? {
        WebkitMaskImage: `url(${cardbackDamageMaskImg})`,
        maskImage: `url(${cardbackDamageMaskImg})`,
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskSize: '100% 100%',
        maskSize: '100% 100%',
        WebkitMaskMode: 'alpha',
        maskMode: 'alpha',
      }
    : null;

  const damageMaskStyles = damageMaskImg
    ? {
        WebkitMaskImage: `url(${damageMaskImg})`,
        maskImage: `url(${damageMaskImg})`,
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskSize: '100% 100%',
        maskSize: '100% 100%',
        WebkitMaskMode: 'alpha',
        maskMode: 'alpha',
      }
    : null;

  let bgImg;
  let rotation = 0;

  switch (true) {
    case tile === 'floor':
    case tile === 'corridor': {
      const variation = tileVariation;
      const corruptedHere = isCorruptedTile;

      if (variation) {
        bgImg = corruptedHere
          ? variation.corruptedImg || FLOOR_TILESET_PROFILES.corrupted.floorBaseImg
          : variation.normalImg || FLOOR_TILESET_PROFILES.default.floorBaseImg;

        rotation = variation.rotation || 0;
      } else {
        bgImg = corruptedHere
          ? FLOOR_TILESET_PROFILES.corrupted.floorBaseImg
          : FLOOR_TILESET_PROFILES.default.floorBaseImg;
      }
      break;
    }
    case isPrimeLabArtTile(tile):
      bgImg = getPrimeLabTileImg(tile, tileAssets) || corridorImg;
      break;
    case tile === 'rest':
      bgImg = restImg;
      break;
    case tile === 'door-open':
      bgImg = doorOpenImg;
      break;
    case tile === 'door-locked':
      bgImg = doorLockedImg;
      break;
    case tile === RANSACKED_CHEST_TILE:
    case tile === 'chest-open':
      bgImg = chestOpenImg;
      break;
    case tile === 'chest-locked':
    case tile === 'chest-closed':
      bgImg = chestLockedImg;
      break;
    case tile === 'chest-simple-locked':
      bgImg = chestSimpleLockedImg;
      break;
    case tile === 'chest-simple-closed':
      bgImg = chestSimpleClosedImg;
      break;
    case tile === 'chest-simple-open':
      bgImg = chestSimpleOpenImg;
      break;
    case tile === 'chest-rare-locked':
      bgImg = chestRareLockedImg;
      break;
    case tile === 'chest-rare-closed':
      bgImg = chestRareClosedImg;
      break;
    case tile === 'chest-rare-open':
      bgImg = chestRareOpenImg;
      break;
    case tile === 'trap':
      bgImg = FLOOR_TILESET_PROFILES.default.floorBaseImg;
      break;
    case tile === 'brazier':
      bgImg = isBrazierLitAtTile ? brazierImg : brazierOutImg;
      break;
    case tile === 'secret-entrance':
      bgImg = FLOOR_TILESET_PROFILES.default.floorBaseImg;
      break;
    case typeof tile === 'string' && tile.startsWith('statue-'):
      bgImg =
        SECRET_STATUE_TILE_TO_IMG[tile] ||
        currentFloorTilesetProfile?.floorBaseImg ||
        FLOOR_TILESET_PROFILES.default.floorBaseImg;
      break;
    case tile === 'merchant':
      bgImg = merchantImg;
      break;
    case tile === 'well':
      bgImg = wellImg;
      break;
    case tile === 'witch':
      bgImg = witchImg;
      break;
    case typeof tile === 'string' && tile.startsWith('shrine-'):
      bgImg = shrineImg;
      break;
    case tile === 'steps-up':
      bgImg = stepsUpImg;
      break;
    case tile === 'steps-down':
      bgImg = stepsDownImg;
      break;
    case typeof tile === 'string' && tile.startsWith('bookshelf-'):
      bgImg = tile.endsWith('-a') ? loreImg : loreImg2;
      break;
    default: {
      const idx = Math.floor(
        hash01(x * 17 + 5, y * 31 + 11, floorSeed) * corridorImgs.length,
      );
      bgImg = corridorImgs[idx] || corridorImg;
      break;
    }
  }


  const tileBackgroundImage = (() => {
    if (!isStoneTilesetTarget || lowFx) {
      return `url(${bgImg})`;
    }

    const layers = [];

    if (currentFloorTilesetProfile?.overlay) {
      layers.push(currentFloorTilesetProfile.overlay);
    }

    if (revealed) {
      layers.push(aoLayer);
      grimeLayersMemo.forEach((g) => {
        layers.push(g);
      });
    }

    layers.push(`url(${bgImg})`);
    return layers.join(', ');
  })();

  const tileBackgroundBlendMode = (() => {
    if (!isStoneTilesetTarget || lowFx) {
      return 'normal';
    }

    const blends = [];

    if (currentFloorTilesetProfile?.overlay) {
      blends.push(
        currentFloorTilesetProfile.overlayBlendMode || 'multiply',
      );
    }

    if (revealed) {
      blends.push('multiply', 'multiply', 'multiply');
    }

    blends.push('normal');
    return blends.join(', ');
  })();

  return (
    <>
      <div
        className="w-full h-full rounded-md absolute backface-hidden overflow-hidden"
        style={{
          zIndex: revealed ? 0 : 2,
          borderRadius: renderTileRadius,
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          transformStyle: 'preserve-3d',
          WebkitTransformStyle: 'preserve-3d',
          border: cardbackDamageMaskStyles ? 'none' : '1px solid black',
          boxShadow: cardbackDamageMaskStyles
            ? 'inset 0 0 0 1px rgba(0, 0, 0, 0.95)'
            : undefined,
          ...cardbackDamageMaskStyles,
          transform: `rotateY(${revealed ? 180 : 0}deg)`,
          opacity: revealed ? 0 : 1,
          transition: revealed
            ? 'transform 500ms ease, opacity 10ms linear 240ms'
            : 'transform 500ms ease, opacity 10ms linear 0ms',
          willChange: 'transform, opacity',
        }}
      >
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{
            borderRadius: renderTileRadius,
            backgroundImage: `url(${cardbackImg})`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            ...cardbackDamageMaskStyles,
          }}
        />

        {cardbackDamageMaskOverlayImg && cardbackDamageMaskStyles && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              borderRadius: renderTileRadius,
              backgroundImage: `url(${cardbackDamageMaskOverlayImg})`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '100% 100%',
              opacity: 0.4,
              ...cardbackDamageMaskStyles,
            }}
          />
        )}
      </div>

      <div
        className="w-full h-full absolute backface-hidden overflow-hidden"
        style={{
          borderRadius: renderTileRadius,
          transform: `${revealed ? 'rotateY(0deg)' : 'rotateY(-180deg)'} rotate(${rotation}deg)`,
          zIndex: 1,
          filter: 'var(--tile-dynamic-filter, none)',
          border: damageMaskStyles ? 'none' : '1px solid black',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          transformStyle: 'preserve-3d',
          WebkitTransformStyle: 'preserve-3d',
          transition: 'transform 500ms ease',
          willChange: 'transform',
        }}
      >
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{
            borderRadius: renderTileRadius,
            backgroundImage: tileBackgroundImage,
            backgroundBlendMode: tileBackgroundBlendMode,
            boxShadow: damageMaskStyles
              ? 'inset 0 0 0 1px rgba(0, 0, 0, 0.95)'
              : undefined,
            ...damageMaskStyles,
          }}
        />

        {damageMaskOverlayImg && damageMaskStyles && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              borderRadius: renderTileRadius,
              backgroundImage: `url(${damageMaskOverlayImg})`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '100% 100%',
              opacity: 0.4,
              ...damageMaskStyles,
            }}
          />
        )}
      </div>
    </>
  );
}, areStaticDungeonTileFacePropsEqual);

const DynamicDungeonTileOverlay = React.memo(function DynamicDungeonTileOverlay({
  x,
  y,
  tile,
  tileCoordKey,
  isPlayer,
  isVisible,
  revealed,
  lowFx,
  aggressiveLowFx,
  lightBucket,
  hasSpiderweb,
  spiderwebShakeTick,
  spiderwebShakeDurationMs,
  renderTileRadius,
  torchProgress,
  brazierGuideOpacity,
  isBrazierLitAtTile,
  ghostEnemyFlickerForTile,
  tileAssets,
  brazierDelay,
  brazierDur,
  ghostEnemyImg,
}) {
  const {
    merchantImg,
    witchImg,
    secretEntranceImg,
    spiderwebImg,
  } = tileAssets;

  const darknessOpacity = 0;
  const warmLightOpacity = 0;
  const midFx = lowFx && !aggressiveLowFx;

  const brazierGuideBlurPx = lowFx ? 0 : (torchProgress === 0 ? 0.9 : 0.35);

  const showBrazierGuideTint =
    brazierGuideOpacity > 0 &&
    revealed &&
    isVisible &&
    tile !== 'non-walkable' &&
    tile !== 'hidden-corridor';

  const isGhostEnemyTile = isVisible && !!ghostEnemyFlickerForTile;

  const spiderwebOverlay = hasSpiderweb && isVisible ? (
    <div
      key={`spiderweb-${tileCoordKey}-${spiderwebShakeTick}`}
      className="absolute inset-0 pointer-events-none"
      style={{
        zIndex: 34,
        borderRadius: renderTileRadius,
        overflow: 'hidden',
        animation:
          spiderwebShakeTick > 0
            ? `dungeonSpiderwebShake ${Math.max(180, Number(spiderwebShakeDurationMs) || 520)}ms ease-in-out 1`
            : 'none',
        transform: 'translate3d(0,0,0)',
        willChange: spiderwebShakeTick > 0 ? 'transform' : 'auto',
      }}
    >
      <img
        className="w-full h-full"
        src={spiderwebImg}
        alt=""
        draggable={false}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          display: 'block',
          opacity: 0.95,
        }}
      />
    </div>
  ) : null;

  return (
    <>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          zIndex: 2,
          borderRadius: renderTileRadius,
          background: `rgba(0, 0, 0, ${darknessOpacity})`,
        }}
      />

      {warmLightOpacity > 0 && !aggressiveLowFx && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            zIndex: 3,
            borderRadius: renderTileRadius,
            background:
              'radial-gradient(circle at 50% 50%, rgba(255,210,145,0.95) 0%, rgba(255,165,90,0.45) 42%, rgba(0,0,0,0) 78%)',
            opacity: warmLightOpacity,
            mixBlendMode: 'screen',
            filter: lowFx ? 'none' : 'blur(1px)',
          }}
        />
      )}

      {isPlayer && torchProgress > 0 && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            zIndex: 1,
            borderRadius: renderTileRadius,
            boxShadow: aggressiveLowFx
              ? 'inset 0 0 0 2px rgba(255, 215, 0, 0.72), 0 0 0 1px rgba(255, 180, 0, 0.55)'
              : midFx
                ? 'inset 0 0 0 2px rgba(255, 215, 0, 0.66), 0 0 8px 1px rgba(255, 195, 70, 0.52)'
                : '0 0 10px 5px rgba(255, 215, 0, 0.6)',
            filter: lowFx ? 'none' : 'blur(3px)',
            opacity: aggressiveLowFx ? 0.92 : 1,
            animation: aggressiveLowFx ? 'pulse 1.9s infinite' : 'pulse 1.5s infinite',
          }}
        />
      )}

      {showBrazierGuideTint && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            zIndex: 4,
            borderRadius: renderTileRadius,
            background: aggressiveLowFx
              ? 'radial-gradient(circle at 50% 50%, rgba(255,210,135,0.42) 0%, rgba(214,138,68,0.2) 68%, rgba(0,0,0,0) 100%)'
              : lowFx
                ? 'radial-gradient(circle at 50% 50%, rgba(255,225,155,0.68) 0%, rgba(242,170,90,0.34) 60%, rgba(0,0,0,0) 100%)'
                : 'radial-gradient(circle at 50% 50%, rgba(255,230,170,0.95) 0%, rgba(242,170,90,0.58) 42%, rgba(166,95,45,0.22) 72%, rgba(0,0,0,0) 100%)',
            opacity: aggressiveLowFx
              ? Math.min(0.52, Math.max(0.22, brazierGuideOpacity * 1.05))
              : lowFx
                ? Math.min(0.58, Math.max(0.2, brazierGuideOpacity * 1.15))
                : brazierGuideOpacity,
            filter:
              aggressiveLowFx || brazierGuideBlurPx <= 0
                ? 'none'
                : `blur(${brazierGuideBlurPx}px)`,
            boxShadow: aggressiveLowFx
              ? `inset 0 0 0 1px rgba(255,190,95,${Math.min(0.34, Math.max(0.18, brazierGuideOpacity))}), 0 0 0 1px rgba(255,165,75,${Math.min(0.18, Math.max(0.1, brazierGuideOpacity * 0.8))})`
              : `inset 0 0 0 1px rgba(255,190,95,${Math.min(lowFx ? 0.34 : 0.35, brazierGuideOpacity)})`,
            transition: 'opacity 160ms ease',
            willChange: 'opacity',
          }}
        />
      )}

      {isVisible && (tile === 'merchant' || tile === 'witch') && (
        <div
          className="absolute inset-0 rounded-md overflow-hidden"
          style={{
            zIndex: 5,
            borderRadius: renderTileRadius,
            overflow: 'hidden',
            backgroundColor: 'transparent',
            transform: 'translateZ(0)',
            pointerEvents: 'none',
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 200ms ease 450ms',
            willChange: isVisible ? 'opacity' : 'auto',
          }}
        >
          <img
            className="w-full h-full"
            src={tile === 'witch' ? witchImg : merchantImg}
            alt=""
            draggable={false}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              display: 'block',
              transform: 'translateZ(0)',
            }}
          />
        </div>
      )}

      {isVisible && tile === 'secret-entrance' && (
        <div
          className="absolute inset-0 rounded-md overflow-hidden"
          style={{
            zIndex: 6,
            borderRadius: renderTileRadius,
            pointerEvents: 'none',
            backgroundColor: 'transparent',
            transform: 'translateZ(0)',
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 150ms ease',
            willChange: isVisible ? 'opacity' : 'auto',
          }}
        >
          <img
            className="w-full h-full"
            src={secretEntranceImg}
            alt=""
            draggable={false}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              display: 'block',
            }}
          />
        </div>
      )}

      <AnimatePresence initial={false}>
        {isGhostEnemyTile && ghostEnemyImg && (
          <motion.div
            key={`ghost-enemy-${ghostEnemyFlickerForTile.id}`}
            className="absolute inset-0 pointer-events-none overflow-hidden"
            style={{
              zIndex: 24,
              borderRadius: renderTileRadius,
            }}
            initial={{
              opacity: 0,
              scale: 0.84,
              x: (ghostEnemyFlickerForTile.shiftX || 0) * -0.35,
              y: 12 + (ghostEnemyFlickerForTile.shiftY || 0) * -0.22,
              filter: `blur(${(ghostEnemyFlickerForTile.blurPx || 0.3) + 1.6}px) brightness(1.45) contrast(1.34) saturate(0.18)`,
            }}
            animate={{
              opacity: ghostEnemyFlickerForTile.opacity || 0.55,
              scale: ghostEnemyFlickerForTile.scale || 1,
              x: ghostEnemyFlickerForTile.shiftX || 0,
              y: ghostEnemyFlickerForTile.shiftY || 0,
              filter: `blur(${ghostEnemyFlickerForTile.blurPx || 0.3}px) brightness(1.04) contrast(1.28) saturate(0.22)`,
            }}
            exit={{
              opacity: 0,
              scale: (ghostEnemyFlickerForTile.scale || 1) * 1.08,
              x: (ghostEnemyFlickerForTile.shiftX || 0) * 1.3,
              y: (ghostEnemyFlickerForTile.shiftY || 0) - 12,
              filter: `blur(${(ghostEnemyFlickerForTile.blurPx || 0.3) + 2.6}px) brightness(1.22) contrast(1.12) saturate(0.14)`,
            }}
            transition={{
              duration: 0.18,
              ease: 'easeOut',
            }}
          >
            {!lowFx && (
              <div
                className="absolute inset-0"
                style={{
                  background: `radial-gradient(circle at 50% 56%, rgba(176, 118, 255, ${Math.min(0.52, (ghostEnemyFlickerForTile.glowOpacity || 0.24) + 0.08)}) 0%, rgba(110, 60, 225, ${ghostEnemyFlickerForTile.glowOpacity || 0.24}) 34%, rgba(0,0,0,0) 72%)`,
                  mixBlendMode: 'screen',
                }}
              />
            )}

            <img
              className="w-full h-full"
              src={ghostEnemyImg}
              alt=""
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block',
                opacity: 0.96,
                transform: 'translateZ(0)',
                filter:
                  'grayscale(1) brightness(0.68) contrast(1.42) saturate(0.18) sepia(0.28) hue-rotate(238deg)',
              }}
            />

            {!lowFx && (
              <img
                className="absolute inset-0 w-full h-full"
                src={ghostEnemyImg}
                alt=""
                draggable={false}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  display: 'block',
                  opacity: Math.min(0.55, (ghostEnemyFlickerForTile.opacity || 0.55) * 0.8),
                  transform: `translate(${ghostEnemyFlickerForTile.echoX || 0}px, ${ghostEnemyFlickerForTile.echoY || 0}px) scale(1.045)`,
                  filter: 'blur(3px) brightness(1.24) contrast(1.15) saturate(0.24)',
                  mixBlendMode: 'screen',
                }}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {spiderwebOverlay}

      {tile === 'trap' && isVisible && (
        <div
          className="absolute inset-0 pointer-events-none flex items-center justify-center"
          style={{ zIndex: 25 }}
        >
          <div
            className="flex items-center justify-center"
            style={{
              width: '60%',
              height: '60%',
              borderRadius: '9999px',
              background:
                'radial-gradient(circle, rgba(140,0,0,0.55) 0%, rgba(0,0,0,0.0) 70%)',
              border: '2px solid rgba(255,255,255,0.22)',
              boxShadow: '0 0 18px rgba(0,0,0,0.55)',
            }}
          >
            <div
              style={{
                color: 'rgba(255,255,255,0.92)',
                fontSize: 28,
                fontWeight: 900,
                textShadow: '0 2px 10px rgba(0,0,0,0.8)',
                transform: 'translateY(-1px)',
              }}
            >
              !
            </div>
          </div>
        </div>
      )}

      {!lowFx && tile === 'brazier' && isBrazierLitAtTile && isVisible && torchProgress === 0 && (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(255, 200, 100, 0.7) 0%, rgba(255, 150, 0, 0.4) 40%, rgba(0, 0, 0, 0) 80%)',
            borderRadius: '50%',
            mixBlendMode: 'screen',
            zIndex: 30,
            filter: 'blur(8px)',
            opacity: 0.6,
            transform: 'scale(1)',
            animation: `brazierFlicker ${brazierDur} ease-in-out ${brazierDelay} infinite alternate`,
            willChange: 'opacity, transform, filter',
          }}
        />
      )}

      {tile === 'brazier' && isBrazierLitAtTile && isVisible && (
        aggressiveLowFx ? (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(255, 196, 104, 0.52) 0%, rgba(255, 145, 32, 0.18) 44%, rgba(0, 0, 0, 0) 68%)',
              borderRadius: '50%',
              zIndex: 31,
              opacity: torchProgress === 0 ? 0.58 : 0.42,
              transition: 'opacity 0.3s ease',
            }}
          />
        ) : lowFx ? (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(255, 200, 100, 0.72) 0%, rgba(255, 150, 0, 0.36) 42%, rgba(0, 0, 0, 0) 72%)',
              borderRadius: '50%',
              zIndex: 31,
              filter: 'blur(3px)',
              opacity: torchProgress === 0 ? 0.72 : 0.55,
              transform: `scale(${torchProgress === 0 ? 1.08 : 1})`,
              transition: 'opacity 0.3s ease, transform 0.3s ease',
            }}
          />
        ) : (
          <>
            <div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              style={{
                background:
                  'radial-gradient(circle, rgba(255, 200, 100, 0.7) 0%, rgba(255, 150, 0, 0.4) 40%, rgba(0, 0, 0, 0) 70%)',
                borderRadius: '50%',
                mixBlendMode: 'screen',
                zIndex: 31,
                filter: `blur(${torchProgress === 0 ? 8 : 4}px)`,
                opacity: torchProgress === 0 ? 0.8 : 0.6,
                transform: `scale(${torchProgress === 0 ? 1.2 : 1})`,
                transition: 'opacity 0.3s ease, transform 0.3s ease',
              }}
            />
            {torchProgress === 0 && (
              <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                style={{
                  background:
                    'radial-gradient(circle, rgba(255, 220, 120, 0.8) 0%, rgba(255, 180, 50, 0.5) 50%, rgba(0, 0, 0, 0) 80%)',
                  borderRadius: '50%',
                  mixBlendMode: 'screen',
                  zIndex: 32,
                  filter: 'blur(6px)',
                  opacity: 0.7,
                  transform: 'scale(1.1)',
                  animation: `brazierFlicker ${brazierDur} ease-in-out ${brazierDelay} infinite alternate`,
                  willChange: 'opacity, transform, filter',
                }}
              />
            )}
          </>
        )
      )}
    </>
  );
}, areDynamicDungeonTileOverlayPropsEqual);

function areStaticDungeonTileFacePropsEqual(prev, next) {
  return (
    prev.x === next.x &&
    prev.y === next.y &&
    prev.tile === next.tile &&
    prev.revealed === next.revealed &&
    prev.floorSeed === next.floorSeed &&
    prev.lowFx === next.lowFx &&
    prev.tileVariation === next.tileVariation &&
    prev.currentFloorTilesetProfile === next.currentFloorTilesetProfile &&
    prev.renderTileRadius === next.renderTileRadius &&
    prev.isCorruptedTile === next.isCorruptedTile &&
    prev.isBrazierLitAtTile === next.isBrazierLitAtTile &&
    prev.tileAssets === next.tileAssets
  );
}

function areDynamicDungeonTileOverlayPropsEqual(prev, next) {
  return (
    prev.x === next.x &&
    prev.y === next.y &&
    prev.tile === next.tile &&
    prev.tileCoordKey === next.tileCoordKey &&
    prev.isPlayer === next.isPlayer &&
    prev.isVisible === next.isVisible &&
    prev.revealed === next.revealed &&
    prev.lowFx === next.lowFx &&
    prev.aggressiveLowFx === next.aggressiveLowFx &&
    prev.lightBucket === next.lightBucket &&
    prev.hasSpiderweb === next.hasSpiderweb &&
    prev.spiderwebShakeTick === next.spiderwebShakeTick &&
    prev.spiderwebShakeDurationMs === next.spiderwebShakeDurationMs &&
    prev.renderTileRadius === next.renderTileRadius &&
    prev.torchProgress === next.torchProgress &&
    prev.brazierGuideOpacity === next.brazierGuideOpacity &&
    prev.isBrazierLitAtTile === next.isBrazierLitAtTile &&
    prev.ghostEnemyFlickerForTile === next.ghostEnemyFlickerForTile &&
    prev.tileAssets === next.tileAssets &&
    prev.brazierDelay === next.brazierDelay &&
    prev.brazierDur === next.brazierDur &&
    prev.ghostEnemyImg === next.ghostEnemyImg
  );
}

function DungeonTile({
  x,
  y,
  tile,
  map,

  // tile-local state
  tileCoordKey,
  isPlayer,
  isVisible,
  isExplored,
  hasSpiderweb,
  spiderwebShakeTick,

  // render state
  isMobile,
  lowFx,
  aggressiveLowFx,
  isStorylineActive,
  floorLevel,
  floorSeed,
  torchProgress,
  brazierGuideRaw,
  tileVariation,
  currentFloorTilesetProfile,
  renderTileWidth,
  renderTileHeight,
  renderTileHalfMargin,
  renderTileRadius,
  runSeed,
  brazierGuideMinOpacity,
  brazierGuideMaxOpacity,
  isBrazierLitAtTile,
  isSpecialCorruptedEnemyTile,
  isYahakaTile,
  isCorruptedTile,
  ghostEnemyFlickerForTile,
  spiderwebShakeDurationMs,
  lightBucket: precomputedLightBucket,
  torchFalloff = 0,
  storylineShiftX,
  storylineShiftY,
  isYahakaGhostForm,
  specialBossEnemyId,
  primeVesselDestroyedDeviceOverlays = [],

  // actions + assets
  handleTileClick,
  tileAssets,
}) {
  const revealed = isExplored;

  const {
    corridorImg,
    corridorImgs,
    restImg,
    doorOpenImg,
    doorLockedImg,
    cardbackImg,
    chestOpenImg,
    chestLockedImg,
    chestSimpleClosedImg,
    chestSimpleLockedImg,
    chestSimpleOpenImg,
    chestRareClosedImg,
    chestRareLockedImg,
    chestRareOpenImg,
    brazierImg,
    brazierOutImg,
    merchantImg,
    shrineImg,
    wellImg,
    witchImg,
    cultistEnemyImg,
    hexbinderEnemyImg,
    nestedHorrorEnemyImg,
    dreadbrandEnemyImg,
    corruptedCultistEnemyImg,
    chainedPenitentEnemyImg,
    chainedPenitentEnemy2Img,
    chainedPenitentEnemy3Img,
    monsignorEnemyImg,
    darkApostleEnemyImg,
    chymistEnemyImg,
    mercenaryEnemyImg,
    lampKeeperEnemyImg,
    custodianEnemyImg,
    blackhandEnemyImg,
    primeVesselEnemyImg,
    machinePrimeCrucible1Img,
    machinePulseEngineImg,
    machineSynapticRetortImg,
    machineAstralReliquaryImg,
    yahakaEnemyImg,
    stepsUpImg,
    stepsDownImg,
    loreImg,
    loreImg2,
    storylineImg,
    secretEntranceImg,
    damageMasks,
    damageMaskOverlays,
    spiderwebImg,
  } = tileAssets;

  const spiderwebOverlay = hasSpiderweb && isVisible ? (
    <div
      key={`spiderweb-${tileCoordKey}-${spiderwebShakeTick}`}
      className="absolute inset-0 pointer-events-none"
      style={{
        zIndex: 34,
        borderRadius: renderTileRadius,
        overflow: 'hidden',
        animation:
          spiderwebShakeTick > 0
            ? `dungeonSpiderwebShake ${Math.max(180, Number(spiderwebShakeDurationMs) || 520)}ms ease-in-out 1`
            : 'none',
        transform: 'translate3d(0,0,0)',
        willChange: spiderwebShakeTick > 0 ? 'transform' : 'auto',
      }}
    >
      <img
        className="w-full h-full"
        src={spiderwebImg}
        alt=""
        draggable={false}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          display: 'block',
          opacity: 0.95,
        }}
      />
    </div>
  ) : null;

  const shellTranslateX =
    isStorylineActive && !isPlayer
      ? storylineShiftX
      : 0;

  const shellTranslateY =
    isStorylineActive && !isPlayer
      ? storylineShiftY
      : 0;

  const shellOpacity =
    isStorylineActive && !isPlayer
      ? 0
      : 1;

  const shellScale = isPlayer
    ? (isStorylineActive ? 1.2 : 1.15)
    : 1;

  const shellRotate = tileVariation?.containerRotation || 0;

  const shellTransform =
    `translate3d(${shellTranslateX}px, ${shellTranslateY}px, 0) scale(${shellScale}) rotate(${shellRotate}deg)`;

  if (tile === 'non-walkable') {
    return (
      <div
        key={x}
        data-tile="true"
        data-x={x}
        data-y={y}
        style={{
          width: renderTileWidth,
          height: renderTileHeight,
          margin: renderTileHalfMargin,
        }}
        onClick={() => {
          if (!isMobile) handleTileClick(x, y);
        }}
      />
    );
  }

  if (tile === 'hidden-corridor') {
    return (
      <div
        key={x}
        data-tile="true"
        data-x={x}
        data-y={y}
        onClick={() => {
          if (!isMobile) handleTileClick(x, y);
        }}
        className={`relative ${isPlayer ? 'z-10' : ''}`}
        style={{
          width: renderTileWidth,
          height: renderTileHeight,
          margin: renderTileHalfMargin,
        }}
      >
        {isPlayer && torchProgress > 0 && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              zIndex: 1,
              borderRadius: renderTileRadius,
              boxShadow: aggressiveLowFx
                ? 'inset 0 0 0 2px rgba(255, 215, 0, 0.72), 0 0 0 1px rgba(255, 180, 0, 0.55)'
                : lowFx
                  ? 'inset 0 0 0 2px rgba(255, 215, 0, 0.66), 0 0 8px 1px rgba(255, 195, 70, 0.52)'
                  : '0 0 10px 5px rgba(255, 215, 0, 0.6)',
              filter: lowFx ? 'none' : 'blur(3px)',
              opacity: aggressiveLowFx ? 0.92 : 1,
              animation: aggressiveLowFx ? 'pulse 1.9s infinite' : 'pulse 1.5s infinite',
            }}
          />
        )}
      </div>
    );
  }

  const isVisibleBrazier =
    tile === 'brazier' && isBrazierLitAtTile && isVisible && torchProgress === 0;

  const bf = isVisibleBrazier ? hash01(x, y, floorSeed) : 0;
  const brazierDelay = `${(bf * 0.9).toFixed(2)}s`;
  const brazierDur = `${(0.7 + bf * 0.8).toFixed(2)}s`;

  const brazierGuideContextStrength = torchProgress === 0 ? 1 : 0.8;

  const brazierGuideOpacity =
    brazierGuideRaw > 0
      ? (
          brazierGuideMinOpacity +
          (brazierGuideMaxOpacity - brazierGuideMinOpacity) * brazierGuideRaw
        ) * brazierGuideContextStrength
      : 0;

  const lightBucket = precomputedLightBucket ?? LIGHT_BUCKET_HIDDEN;

const specialBossEnemyIdAtTile = getSpecialBossFloorEnemyIdAtTile(
  specialBossEnemyId,
  {
    x,
    y,
    tile,
  }
);

const enemyIdAtTile =
  tile === 'enemy-boss'
    ? (typeof specialBossEnemyId === 'string' && specialBossEnemyId.trim()
        ? specialBossEnemyId.trim()
        : 'nested_horror')
    : specialBossEnemyIdAtTile
      ? specialBossEnemyIdAtTile
      : isYahakaTile
        ? 'yahaka_voidfeeder'
        : isSpecialCorruptedEnemyTile
          ? CORRUPTED_SPECIAL_ENEMY_ID
          : getEnemyIdForTile({
              x,
              y,
              floor: floorLevel,
              runSeed,
              map,
            });

const isMonsignorFloorGuardTile =
  specialBossEnemyId === 'monsignor' &&
  specialBossEnemyIdAtTile === 'chained-penitent';
const isLiberatedMonsignorGuardTile = tile === MONSIGNOR_LIBERATED_GUARD_TILE;

const ENEMY_IMAGES = {
  nested_horror: nestedHorrorEnemyImg,
  hexbinder: hexbinderEnemyImg,
  dreadbrand: dreadbrandEnemyImg,
  cultist: cultistEnemyImg,
  'chained-penitent': chainedPenitentEnemyImg,
  monsignor: monsignorEnemyImg,
  'dark-apostle': darkApostleEnemyImg,
  chymist: chymistEnemyImg,
  mercenary: mercenaryEnemyImg,
  lamp_keeper: lampKeeperEnemyImg,
  custodian: custodianEnemyImg,
  blackhand: blackhandEnemyImg,
  prime_vessel: primeVesselEnemyImg,
  prime_crucible_device: machinePrimeCrucible1Img,
  pulse_engine_device: machinePulseEngineImg,
  synaptic_retort_device: machineSynapticRetortImg,
  astral_reliquary_device: machineAstralReliquaryImg,
  yahaka_voidfeeder: yahakaEnemyImg,
  [CORRUPTED_SPECIAL_ENEMY_ID]: corruptedCultistEnemyImg,
};

const enemyImg = isLiberatedMonsignorGuardTile
  ? (chainedPenitentEnemy3Img || chainedPenitentEnemy2Img || chainedPenitentEnemyImg)
  : isMonsignorFloorGuardTile
    ? (chainedPenitentEnemy2Img || chainedPenitentEnemyImg)
    : (ENEMY_IMAGES[enemyIdAtTile] ?? cultistEnemyImg);
const isGhostEnemyTile = isVisible && !!ghostEnemyFlickerForTile;
const ghostEnemyImg = isGhostEnemyTile
  ? ENEMY_IMAGES[ghostEnemyFlickerForTile.enemyId] ?? cultistEnemyImg
  : null;
const isPrimeVesselBossTile =
  tile === 'enemy-boss' &&
  enemyIdAtTile === 'prime_vessel';
const visiblePrimeVesselDestroyedOverlays =
  isPrimeVesselBossTile && Array.isArray(primeVesselDestroyedDeviceOverlays)
    ? primeVesselDestroyedDeviceOverlays.filter(Boolean)
    : [];
const yahakaGhostOffsetX = Number((((hash01(x + 41, y - 17, floorSeed) - 0.5) * 6)).toFixed(2));
const yahakaGhostOffsetY = Number((((hash01(x - 13, y + 29, floorSeed) - 0.5) * 5)).toFixed(2));

  const isStoneTilesetTarget =
    tile === 'floor' ||
    tile === 'corridor' ||
    isPrimeLabArtTile(tile) ||
    tile === 'trap' ||
    tile === 'secret-entrance' ||
    (typeof tile === 'string' && tile.startsWith('statue-'));

  const cardbackDamageMaskIndex =
    Array.isArray(damageMasks) && damageMasks.length > 0
      ? Math.min(
          damageMasks.length - 1,
          Math.floor(hash01(x * 19 + 7, y * 23 + 13, floorSeed) * damageMasks.length),
        )
      : -1;

  const cardbackDamageMaskImg =
    cardbackDamageMaskIndex >= 0 && Array.isArray(damageMasks)
      ? damageMasks[cardbackDamageMaskIndex] || null
      : null;

  const cardbackDamageMaskOverlayImg =
    cardbackDamageMaskIndex >= 0 && Array.isArray(damageMaskOverlays)
      ? damageMaskOverlays[cardbackDamageMaskIndex] || null
      : null;

  const damageMaskIndex =
    isStoneTilesetTarget && cardbackDamageMaskIndex >= 0
      ? cardbackDamageMaskIndex
      : -1;

  const damageMaskImg =
    damageMaskIndex >= 0 && Array.isArray(damageMasks)
      ? damageMasks[damageMaskIndex] || null
      : null;

  const damageMaskOverlayImg =
    damageMaskIndex >= 0 && Array.isArray(damageMaskOverlays)
      ? damageMaskOverlays[damageMaskIndex] || null
      : null;

  const cardbackDamageMaskStyles = cardbackDamageMaskImg
    ? {
        WebkitMaskImage: `url(${cardbackDamageMaskImg})`,
        maskImage: `url(${cardbackDamageMaskImg})`,
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskSize: '100% 100%',
        maskSize: '100% 100%',
        WebkitMaskMode: 'alpha',
        maskMode: 'alpha',
      }
    : null;

  const damageMaskStyles = damageMaskImg
    ? {
        WebkitMaskImage: `url(${damageMaskImg})`,
        maskImage: `url(${damageMaskImg})`,
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskSize: '100% 100%',
        maskSize: '100% 100%',
        WebkitMaskMode: 'alpha',
        maskMode: 'alpha',
      }
    : null;

  const tilesetFilter = isStoneTilesetTarget
    ? currentFloorTilesetProfile?.filter || FLOOR_TILESET_PROFILES.default.filter
    : FLOOR_TILESET_PROFILES.default.filter;

  const isLitBrazierTile = tile === 'brazier' && isBrazierLitAtTile;
  const isLightSource = isLitBrazierTile || tile === 'rest';
  const ambientBaseLit = isLightSource ? 0.8 : 0.45;
  const baseLit = torchProgress > 0 ? Math.max(0, Number(torchFalloff) || 0) : ambientBaseLit;
  const rawBrightness = 0.38 + baseLit * 0.7;
  const brightnessCap = isLightSource ? 1.05 : 1.18;
  const litBrightness = Math.min(brightnessCap, Math.max(0.28, rawBrightness));
  const brightness = !revealed
    ? 0.22
    : isVisible
      ? litBrightness
      : isLightSource
        ? 0.32
        : 0.26;
  const contrast = !revealed
    ? 1
    : isVisible
      ? isLightSource
        ? 1.02
        : 1 + baseLit * 0.2
      : 1;
  const saturate = !revealed
    ? 0.8
    : isVisible
      ? isLightSource
        ? 0.98
        : 0.9 + baseLit * 0.25
      : 0.75;

  const isStone = tile === 'floor' || tile === 'corridor' || isPrimeLabArtTile(tile);
  const nA = isStone ? hash01(x, y, floorSeed) : 0;
  const nB = isStone ? hash01(x + 17, y - 23, floorSeed) : 0;
  const nC = isStone ? hash01(x - 31, y + 7, floorSeed) : 0;

  const brightnessVar = isVisible && isStone ? 0.96 + (nA - 0.5) * 0.08 : 1;
  const saturateVar = isVisible && isStone ? 0.96 + (nB - 0.5) * 0.06 : 1;
  const hueVar = isVisible && isStone ? (nC - 0.5) * 10 : 0;

  const tileBrightness = brightness * brightnessVar;
  const tileSaturate = saturate * saturateVar;
  const showBrazierGuideTint =
    brazierGuideOpacity > 0 &&
    revealed &&
    isVisible &&
    tile !== 'non-walkable' &&
    tile !== 'hidden-corridor';
  const brazierGuideFx = showBrazierGuideTint
    ? Math.min(1, brazierGuideRaw * (torchProgress === 0 ? 1.35 : 1.0))
    : 0;

  const tileBrightnessWithBrazierGuide = Math.min(
    2.0,
    tileBrightness + brazierGuideFx * 0.55,
  );
  const tileSaturateWithBrazierGuide = Math.min(
    2.0,
    tileSaturate + brazierGuideFx * 0.45,
  );
  const tileSepiaWithBrazierGuide = Math.min(0.85, brazierGuideFx * 0.7);
  const tileHueWithBrazierGuide = hueVar - brazierGuideFx * 6;
  const lowFxTileBrightnessWithBrazierGuide = Math.max(
    0.55,
    Math.min(
      1.3,
      tileBrightness * (tilesetFilter.brightness ?? 1) + brazierGuideFx * 0.32,
    ),
  );
  const lowFxTileSaturateWithBrazierGuide = Math.max(
    0.9,
    Math.min(
      1.35,
      tileSaturate * (tilesetFilter.saturate ?? 1) + brazierGuideFx * 0.2,
    ),
  );
  const lowFxTileSepiaWithBrazierGuide = Math.max(
    0,
    Math.min(0.28, (tilesetFilter.sepia ?? 0) + brazierGuideFx * 0.18),
  );
  const lowFxTileHueWithBrazierGuide = hueVar - brazierGuideFx * 3.5 + (tilesetFilter.hueRotate ?? 0);

  const aggressiveLowFxTileBrightnessWithBrazierGuide = Math.max(
    0.82,
    Math.min(1.22, 1 + brazierGuideFx * 0.16),
  );
  const aggressiveLowFxTileSaturateWithBrazierGuide = Math.max(
    0.95,
    Math.min(1.18, 1 + brazierGuideFx * 0.1),
  );

  const dynamicTileFaceFilter = aggressiveLowFx
    ? `brightness(${aggressiveLowFxTileBrightnessWithBrazierGuide}) saturate(${aggressiveLowFxTileSaturateWithBrazierGuide})`
    : lowFx
      ? `brightness(${lowFxTileBrightnessWithBrazierGuide}) saturate(${lowFxTileSaturateWithBrazierGuide}) sepia(${lowFxTileSepiaWithBrazierGuide}) hue-rotate(${lowFxTileHueWithBrazierGuide}deg)`
      : `brightness(${tileBrightnessWithBrazierGuide * (tilesetFilter.brightness ?? 1)}) contrast(${contrast * (tilesetFilter.contrast ?? 1)}) saturate(${tileSaturateWithBrazierGuide * (tilesetFilter.saturate ?? 1)}) sepia(${Math.max(0, Math.min(1, tileSepiaWithBrazierGuide + (tilesetFilter.sepia ?? 0)))}) hue-rotate(${tileHueWithBrazierGuide + (tilesetFilter.hueRotate ?? 0)}deg)`;

  let bgImg;
  let rotation = 0;

  switch (true) {
    case tile === 'floor':
    case tile === 'corridor': {
      const variation = tileVariation;
      const corruptedHere = isCorruptedTile;

      if (variation) {
        bgImg = corruptedHere
          ? variation.corruptedImg || FLOOR_TILESET_PROFILES.corrupted.floorBaseImg
          : variation.normalImg || FLOOR_TILESET_PROFILES.default.floorBaseImg;

        rotation = variation.rotation || 0;
      } else {
        bgImg = corruptedHere
          ? FLOOR_TILESET_PROFILES.corrupted.floorBaseImg
          : FLOOR_TILESET_PROFILES.default.floorBaseImg;
      }
      break;
    }
    case isPrimeLabArtTile(tile):
      bgImg = getPrimeLabTileImg(tile, tileAssets) || corridorImg;
      break;
    case tile === 'rest':
      bgImg = restImg;
      break;
    case tile === 'door-open':
      bgImg = doorOpenImg;
      break;
    case tile === 'door-locked':
      bgImg = doorLockedImg;
      break;
    case tile === RANSACKED_CHEST_TILE:
      bgImg = chestOpenImg;
      break;
    case tile === 'chest-open':
      bgImg = chestOpenImg;
      break;
    case tile === 'chest-locked':
    case tile === 'chest-closed':
      bgImg = chestLockedImg;
      break;
    case tile === 'chest-simple-locked':
      bgImg = chestSimpleLockedImg;
      break;
    case tile === 'chest-simple-closed':
      bgImg = chestSimpleClosedImg;
      break;
    case tile === 'chest-simple-open':
      bgImg = chestSimpleOpenImg;
      break;
    case tile === 'chest-rare-locked':
      bgImg = chestRareLockedImg;
      break;
    case tile === 'chest-rare-closed':
      bgImg = chestRareClosedImg;
      break;
    case tile === 'chest-rare-open':
      bgImg = chestRareOpenImg;
      break;
    case tile === 'trap':
      bgImg = FLOOR_TILESET_PROFILES.default.floorBaseImg;
      break;
    case tile === 'brazier':
      bgImg = isBrazierLitAtTile ? brazierImg : brazierOutImg;
      break;
    case tile === 'secret-entrance':
      bgImg = FLOOR_TILESET_PROFILES.default.floorBaseImg;
      break;
    case typeof tile === 'string' && tile.startsWith('statue-'):
      bgImg =
        SECRET_STATUE_TILE_TO_IMG[tile] ||
        currentFloorTilesetProfile?.floorBaseImg ||
        FLOOR_TILESET_PROFILES.default.floorBaseImg;
      break;
    case tile === 'merchant':
      bgImg = merchantImg;
      break;
    case tile === 'well':
      bgImg = wellImg;
      break;
    case tile === 'witch':
      bgImg = witchImg;
      break;
    case typeof tile === 'string' && tile.startsWith('shrine-'):
      bgImg = shrineImg;
      break;
    case tile === 'enemy-boss':
    case tile === 'enemy':
    case tile === MONSIGNOR_LIBERATED_GUARD_TILE:
      return (
        <motion.div
          key={x}
          data-tile="true"
          data-x={x}
          data-y={y}
          className="relative"
          style={{
            width: renderTileWidth,
            height: renderTileHeight,
            margin: renderTileHalfMargin,
            zIndex: isPlayer ? 100 : 1,
          }}
          animate={{
            scale: 1,
            opacity:
              isStorylineActive && !isPlayer
                ? 0
                : 1,
            x:
              isStorylineActive && !isPlayer
                ? storylineShiftX
                : 0,
            y:
              isStorylineActive && !isPlayer
                ? storylineShiftY
                : 0,
          }}
          transition={{
            duration: 0.3,
            ease: 'easeInOut',
          }}
          onClick={() => {
            if (!isMobile) handleTileClick(x, y);
          }}
        >
          <motion.div
            className="w-full h-full rounded-md absolute backface-hidden overflow-hidden"
            style={{
              zIndex: revealed ? 0 : 2,
              borderRadius: renderTileRadius,
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transformStyle: 'preserve-3d',
              WebkitTransformStyle: 'preserve-3d',
              border: cardbackDamageMaskStyles ? 'none' : '1px solid black',
              boxShadow: cardbackDamageMaskStyles
                ? 'inset 0 0 0 1px rgba(0, 0, 0, 0.95)'
                : undefined,
              ...cardbackDamageMaskStyles,
            }}
            initial={false}
            animate={{ rotateY: revealed ? 180 : 0 }}
            transition={{ duration: 0.5 }}
          >
            <div
              className="absolute inset-0 bg-center bg-cover"
              style={{
                borderRadius: renderTileRadius,
                backgroundImage: `url(${cardbackImg})`,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                ...cardbackDamageMaskStyles,
              }}
            />

            {cardbackDamageMaskOverlayImg && cardbackDamageMaskStyles && (
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  borderRadius: renderTileRadius,
                  backgroundImage: `url(${cardbackDamageMaskOverlayImg})`,
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '100% 100%',
                  opacity: 0.4,
                  ...cardbackDamageMaskStyles,
                }}
              />
            )}
          </motion.div>

          {isVisible && !isYahakaGhostForm && (
            <motion.div
              className="absolute inset-0 rounded-md overflow-hidden"
              style={{
                zIndex: 3,
                borderRadius: renderTileRadius,
                overflow: 'hidden',
                backgroundColor: 'transparent',
                transform: 'translateZ(0)',
                willChange: 'opacity',
                pointerEvents: 'none',
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2, delay: 0.45 }}
            >
              <img
                className="w-full h-full"
                src={enemyImg}
                alt=""
                draggable={false}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  display: 'block',
                  transform: 'translateZ(0)',
                }}
              />
              {visiblePrimeVesselDestroyedOverlays.map((overlaySrc, overlayIndex) => (
                <img
                  key={`prime-vessel-destroyed-overlay-${tileCoordKey}-${overlayIndex}`}
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  src={overlaySrc}
                  alt=""
                  draggable={false}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    display: 'block',
                    transform: 'translateZ(0)',
                  }}
                />
              ))}
            </motion.div>
          )}

          <AnimatePresence initial={false}>
            {isYahakaGhostForm && enemyImg && (
              <motion.div
                key={`yahaka-ghost-${tileCoordKey}`}
                className="absolute inset-0 pointer-events-none overflow-hidden"
                style={{
                  zIndex: 24,
                  borderRadius: renderTileRadius,
                }}
                initial={{ opacity: 0.18, scale: 0.96 }}
                animate={{
                  opacity: [0.54, 0.72, 0.58],
                  scale: [0.98, 1.02, 0.985],
                  x: [0, yahakaGhostOffsetX, 0],
                  y: [0, yahakaGhostOffsetY, 0],
                  filter: [
                    'blur(1.9px) brightness(1.08) contrast(1.28) saturate(0.2)',
                    'blur(2.5px) brightness(1.18) contrast(1.34) saturate(0.16)',
                    'blur(2.1px) brightness(1.1) contrast(1.26) saturate(0.18)',
                  ],
                }}
                exit={{ opacity: 0, scale: 1.04 }}
                transition={{
                  duration: 1.8,
                  ease: 'easeInOut',
                  repeat: Infinity,
                  repeatType: 'mirror',
                }}
              >
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      'radial-gradient(circle at 50% 56%, rgba(176, 118, 255, 0.42) 0%, rgba(110, 60, 225, 0.28) 34%, rgba(0,0,0,0) 72%)',
                    mixBlendMode: 'screen',
                  }}
                />

                <img
                  className="w-full h-full"
                  src={enemyImg}
                  alt=""
                  draggable={false}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    display: 'block',
                    opacity: 0.96,
                    transform: 'translateZ(0)',
                    filter:
                      'grayscale(1) brightness(0.68) contrast(1.42) saturate(0.18) sepia(0.28) hue-rotate(238deg)',
                  }}
                />

                <img
                  className="absolute inset-0 w-full h-full"
                  src={enemyImg}
                  alt=""
                  draggable={false}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    display: 'block',
                    opacity: 0.48,
                    transform: 'translate(4px, -6px) scale(1.05)',
                    filter: 'blur(3px) brightness(1.24) contrast(1.15) saturate(0.24)',
                    mixBlendMode: 'screen',
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {spiderwebOverlay}

          <motion.div
            className="w-full h-full border border-black rounded-md bg-center bg-cover absolute backface-hidden"
            style={{
              borderRadius: renderTileRadius,
              backgroundImage: isYahakaGhostForm ? 'none' : `url(${enemyImg})`,
              transform: 'rotateY(180deg)',
              zIndex: 1,
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transformStyle: 'preserve-3d',
              WebkitTransformStyle: 'preserve-3d',
            }}
            initial={false}
            animate={{ rotateY: revealed ? 0 : -180 }}
            transition={{ duration: 0.5 }}
          />
        </motion.div>
      );
    case tile === 'steps-up':
      bgImg = stepsUpImg;
      break;
    case tile === 'steps-down':
      bgImg = stepsDownImg;
      break;
    case typeof tile === 'string' && tile.startsWith('bookshelf-'):
      bgImg = tile.endsWith('-a') ? loreImg : loreImg2;
      break;
    case tile === 'storyline':
      return (
        <motion.div
          key={`${x}-${y}`}
          data-tile="true"
          data-x={x}
          data-y={y}
          className="relative"
          onClick={() => {
            if (!isMobile) handleTileClick(x, y);
          }}
          style={{
            width: renderTileWidth,
            height: renderTileHeight,
            margin: renderTileHalfMargin,
            zIndex: isPlayer ? 100 : 1,
          }}
          animate={{
            x: isStorylineActive ? storylineShiftX : 0,
            y: isStorylineActive ? storylineShiftY : 0,
            opacity: isStorylineActive ? 0 : 1,
            scale: isPlayer ? (isStorylineActive ? 1.2 : 1.15) : 1,
          }}
          transition={{
            type: 'spring',
            stiffness: 50,
            damping: 10,
            duration: 0.5,
          }}
        >
          <div
            className="absolute inset-0 border border-black rounded-md overflow-hidden"
            style={{
              backgroundColor: 'transparent',
              borderRadius: renderTileRadius,
            }}
          >
            <div
              className="absolute inset-0"
              style={{
                transformStyle: 'preserve-3d',
                WebkitTransformStyle: 'preserve-3d',
              }}
            >
              <motion.div
                className="absolute inset-0 overflow-hidden"
                style={{
                  zIndex: 2,
                  borderRadius: renderTileRadius,
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  transformStyle: 'preserve-3d',
                  WebkitTransformStyle: 'preserve-3d',
                  border: cardbackDamageMaskStyles ? 'none' : '1px solid black',
                  boxShadow: cardbackDamageMaskStyles
                    ? 'inset 0 0 0 1px rgba(0, 0, 0, 0.95)'
                    : undefined,
                  ...cardbackDamageMaskStyles,
                }}
                initial={false}
                animate={{ rotateY: revealed ? 180 : 0, opacity: revealed ? 0 : 1 }}
                transition={{
                  rotateY: { duration: 0.5 },
                  opacity: { duration: 0.01, delay: revealed ? 0.24 : 0 },
                }}
              >
                <div
                  className="absolute inset-0 bg-center bg-cover"
                  style={{
                    borderRadius: renderTileRadius,
                    backgroundImage: `url(${cardbackImg})`,
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    ...cardbackDamageMaskStyles,
                  }}
                />

                {cardbackDamageMaskOverlayImg && cardbackDamageMaskStyles && (
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      borderRadius: renderTileRadius,
                      backgroundImage: `url(${cardbackDamageMaskOverlayImg})`,
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '100% 100%',
                      opacity: 0.4,
                      ...cardbackDamageMaskStyles,
                    }}
                  />
                )}
              </motion.div>

              <motion.div
                className="absolute inset-0 bg-center"
                style={{
                  backgroundImage: `url(${storylineImg})`,
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  zIndex: 1,
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                }}
                initial={false}
                animate={{ rotateY: revealed ? 0 : -180 }}
                transition={{ duration: 0.5 }}
              />
            </div>

            {isVisible && (
              <motion.div
                className="absolute inset-0"
                style={{
                  zIndex: 3,
                  borderRadius: renderTileRadius,
                  overflow: 'hidden',
                  backgroundColor: 'transparent',
                  transform: 'translateZ(0)',
                  willChange: 'opacity',
                  pointerEvents: 'none',
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2, delay: 0.45 }}
              >
                <img
                  className="w-full h-full"
                  src={storylineImg}
                  alt=""
                  draggable={false}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    display: 'block',
                    transform: 'translateZ(0)',
                  }}
                />
              </motion.div>
            )}
          </div>
          {spiderwebOverlay}
        </motion.div>
      );
    default: {
      const idx = Math.floor(
        hash01(x * 17 + 5, y * 31 + 11, floorSeed) * corridorImgs.length,
      );
      bgImg = corridorImgs[idx] || corridorImg;
      break;
    }
  }

  return (
    <div
      key={x}
      data-tile="true"
      data-x={x}
      data-y={y}
      className={`${isPlayer ? 'z-10 relative' : ''}`}
      style={{
        width: renderTileWidth,
        height: renderTileHeight,
        margin: renderTileHalfMargin,
        perspective: 600,
        transformStyle: 'preserve-3d',
        WebkitTransformStyle: 'preserve-3d',
        transform: shellTransform,
        opacity: shellOpacity,
        '--tile-dynamic-filter': dynamicTileFaceFilter,
        transition: isStorylineActive
          ? 'transform 500ms cubic-bezier(0.22, 1, 0.36, 1), opacity 300ms ease'
          : 'transform 180ms ease, opacity 180ms linear',
        willChange: isStorylineActive || isPlayer ? 'transform, opacity' : 'auto',
      }}
      onClick={() => {
        if (!isMobile) handleTileClick(x, y);
      }}
    >
      <StaticDungeonTileFace
        x={x}
        y={y}
        tile={tile}
        revealed={revealed}
        floorSeed={floorSeed}
        lowFx={lowFx}
        aggressiveLowFx={aggressiveLowFx}
        tileVariation={tileVariation}
        currentFloorTilesetProfile={currentFloorTilesetProfile}
        renderTileRadius={renderTileRadius}
        isCorruptedTile={isCorruptedTile}
        isBrazierLitAtTile={isBrazierLitAtTile}
        tileAssets={tileAssets}
      />

      <DynamicDungeonTileOverlay
        x={x}
        y={y}
        tile={tile}
        tileCoordKey={tileCoordKey}
        isPlayer={isPlayer}
        isVisible={isVisible}
        revealed={revealed}
        lowFx={lowFx}
        aggressiveLowFx={aggressiveLowFx}
        lightBucket={lightBucket}
        hasSpiderweb={hasSpiderweb}
        spiderwebShakeTick={spiderwebShakeTick}
        spiderwebShakeDurationMs={spiderwebShakeDurationMs}
        renderTileRadius={renderTileRadius}
        torchProgress={torchProgress}
        brazierGuideOpacity={brazierGuideOpacity}
        isBrazierLitAtTile={isBrazierLitAtTile}
        ghostEnemyFlickerForTile={ghostEnemyFlickerForTile}
        tileAssets={tileAssets}
        brazierDelay={brazierDelay}
        brazierDur={brazierDur}
        ghostEnemyImg={ghostEnemyImg}
      />
    </div>
  );

}

function areCommonDungeonTileWrapperPropsEqual(prev, next) {
  return (
    prev.x === next.x &&
    prev.y === next.y &&
    prev.tile === next.tile &&
    prev.tileCoordKey === next.tileCoordKey &&
    prev.isPlayer === next.isPlayer &&
    prev.isVisible === next.isVisible &&
    prev.isExplored === next.isExplored &&
    prev.hasSpiderweb === next.hasSpiderweb &&
    prev.spiderwebShakeTick === next.spiderwebShakeTick &&
    prev.isMobile === next.isMobile &&
    prev.lowFx === next.lowFx &&
    prev.aggressiveLowFx === next.aggressiveLowFx &&
    prev.isStorylineActive === next.isStorylineActive &&
    prev.floorLevel === next.floorLevel &&
    prev.floorSeed === next.floorSeed &&
    prev.torchProgress === next.torchProgress &&
    prev.brazierGuideRaw === next.brazierGuideRaw &&
    prev.tileVariation === next.tileVariation &&
    prev.currentFloorTilesetProfile === next.currentFloorTilesetProfile &&
    prev.renderTileWidth === next.renderTileWidth &&
    prev.renderTileHeight === next.renderTileHeight &&
    prev.renderTileHalfMargin === next.renderTileHalfMargin &&
    prev.renderTileRadius === next.renderTileRadius &&
    prev.runSeed === next.runSeed &&
    prev.brazierGuideMinOpacity === next.brazierGuideMinOpacity &&
    prev.brazierGuideMaxOpacity === next.brazierGuideMaxOpacity &&
    prev.isBrazierLitAtTile === next.isBrazierLitAtTile &&
    prev.isSpecialCorruptedEnemyTile === next.isSpecialCorruptedEnemyTile &&
    prev.isYahakaTile === next.isYahakaTile &&
    prev.isYahakaGhostForm === next.isYahakaGhostForm &&
    prev.specialBossEnemyId === next.specialBossEnemyId &&
    prev.primeVesselDestroyedDeviceOverlays === next.primeVesselDestroyedDeviceOverlays &&
    prev.isCorruptedTile === next.isCorruptedTile &&
    prev.ghostEnemyFlickerForTile === next.ghostEnemyFlickerForTile &&
    prev.spiderwebShakeDurationMs === next.spiderwebShakeDurationMs &&
    prev.lightBucket === next.lightBucket &&
    prev.torchFalloff === next.torchFalloff &&
    prev.storylineShiftX === next.storylineShiftX &&
    prev.storylineShiftY === next.storylineShiftY &&
    prev.handleTileClick === next.handleTileClick &&
    prev.tileAssets === next.tileAssets
  );
}

function areDungeonTilePropsEqual(prev, next) {
  const prevCommon = isCommonDungeonTileType(prev.tile);
  const nextCommon = isCommonDungeonTileType(next.tile);

  if (prevCommon && nextCommon) {
    return areCommonDungeonTileWrapperPropsEqual(prev, next);
  }

  return (
    prev.x === next.x &&
    prev.y === next.y &&
    prev.tile === next.tile &&
    prev.tileCoordKey === next.tileCoordKey &&
    prev.isPlayer === next.isPlayer &&
    prev.isVisible === next.isVisible &&
    prev.isExplored === next.isExplored &&
    prev.hasSpiderweb === next.hasSpiderweb &&
    prev.spiderwebShakeTick === next.spiderwebShakeTick &&
    prev.isMobile === next.isMobile &&
    prev.lowFx === next.lowFx &&
    prev.aggressiveLowFx === next.aggressiveLowFx &&
    prev.isStorylineActive === next.isStorylineActive &&
    prev.floorLevel === next.floorLevel &&
    prev.floorSeed === next.floorSeed &&
    prev.torchProgress === next.torchProgress &&
    prev.tileVariation === next.tileVariation &&
    prev.currentFloorTilesetProfile === next.currentFloorTilesetProfile &&
    prev.renderTileWidth === next.renderTileWidth &&
    prev.renderTileHeight === next.renderTileHeight &&
    prev.renderTileHalfMargin === next.renderTileHalfMargin &&
    prev.renderTileRadius === next.renderTileRadius &&
    prev.runSeed === next.runSeed &&
    prev.isBrazierLitAtTile === next.isBrazierLitAtTile &&
    prev.isSpecialCorruptedEnemyTile === next.isSpecialCorruptedEnemyTile &&
    prev.isYahakaTile === next.isYahakaTile &&
    prev.isYahakaGhostForm === next.isYahakaGhostForm &&
    prev.specialBossEnemyId === next.specialBossEnemyId &&
    prev.primeVesselDestroyedDeviceOverlays === next.primeVesselDestroyedDeviceOverlays &&
    prev.isCorruptedTile === next.isCorruptedTile &&
    prev.ghostEnemyFlickerForTile === next.ghostEnemyFlickerForTile &&
    prev.spiderwebShakeDurationMs === next.spiderwebShakeDurationMs &&
    prev.torchFalloff === next.torchFalloff &&
    prev.storylineShiftX === next.storylineShiftX &&
    prev.storylineShiftY === next.storylineShiftY &&
    prev.handleTileClick === next.handleTileClick &&
    prev.tileAssets === next.tileAssets
  );
}

export default React.memo(DungeonTile, areDungeonTilePropsEqual);
